/**
 * Claim submission (bill-under) settings — GDN-1926
 *
 * Lets a practice admin submit a clinician's claims under another member's
 * NPI (the rendering provider on the claim), either for all payers or with
 * per-payer exceptions. Used while a clinician awaits payer credentialing.
 *
 * Selects save immediately per the autosave pattern. Earnings and reports
 * are unaffected — encounters keep the treating clinician.
 */

import { useCallback, useEffect, useState } from 'react';
import { AutosaveStatus } from '@components/AutosaveStatus';
import { usePayer, usePayers } from '@features/payer-search';
import { useAutoSave } from '@hooks/useAutoSave';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useGetOrganizationMembersQuery, useUpdateMemberBillingInfoMutation } from '../../store/api';
import type { MemberBillingInfo, MemberPayerBillingOverride } from '../../types';

const SELF = '';

interface MemberOption {
  userId: string;
  label: string;
  hasNpi: boolean;
}

/** Local row shape: payer_uuid '' means a draft row not yet persisted. */
interface RuleRow {
  payer_uuid: string;
  bill_under_user_id: string | null;
}

interface ClaimSubmissionSettingsProps {
  userId: string;
  billingInfo?: MemberBillingInfo | null;
  disabled?: boolean;
}

function PayerPicker({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (payerUuid: string) => void;
  disabled?: boolean;
}) {
  const [inputValue, setInputValue] = useState('');
  const { payers, isFetching } = usePayers(
    { search: inputValue || null, perPage: 20 },
    { skip: !inputValue },
  );
  // Resolve the saved payer's display name without requiring a search hit.
  const { payer: selectedPayer } = usePayer(value || null, { skip: !value });

  const options = payers
    .filter(p => p.id)
    .map(p => ({ id: p.id as string, label: p.display_name }));
  const selectedOption = value
    ? (options.find(o => o.id === value) ?? {
        id: value,
        label: selectedPayer?.display_name ?? '…',
      })
    : null;

  return (
    <Autocomplete
      size="small"
      sx={{ flex: 1, minWidth: 0 }}
      options={options}
      value={selectedOption}
      inputValue={inputValue}
      onInputChange={(_, next) => setInputValue(next)}
      onChange={(_, option) => option && onChange(option.id)}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      loading={isFetching}
      disabled={disabled}
      disableClearable={false}
      noOptionsText={inputValue ? 'No payers found' : 'Type to search payers'}
      renderInput={params => (
        <TextField {...params} label="Payer" placeholder="Search payers…" />
      )}
    />
  );
}

export function ClaimSubmissionSettings({
  userId,
  billingInfo,
  disabled = false,
}: ClaimSubmissionSettingsProps) {
  const { clinicians } = useEventTracking();
  const [updateBillingInfo] = useUpdateMemberBillingInfoMutation();
  const { data: membersData } = useGetOrganizationMembersQuery({});

  const [billUnderUserId, setBillUnderUserId] = useState<string>(SELF);
  const [rules, setRules] = useState<RuleRow[]>([]);

  useEffect(() => {
    setBillUnderUserId(billingInfo?.bill_under_user_id ?? SELF);
    setRules(
      (billingInfo?.payer_billing_overrides ?? []).map(rule => ({
        payer_uuid: rule.payer_uuid,
        bill_under_user_id: rule.bill_under_user_id ?? null,
      })),
    );
  }, [billingInfo]);

  // Deliberately any active member, not just the Supervisor role: billing
  // supervision (whose NPI renders the claim) is a payer/credentialing
  // relationship, distinct from the clinical co-sign role — practices often
  // bill under a credentialed colleague who isn't a designated supervisor.
  // Members without an NPI are listed but disabled below.
  const memberOptions: MemberOption[] = (membersData?.members ?? [])
    .filter(m => m.user_id !== userId && m.status === 'active')
    .map(m => ({
      userId: m.user_id,
      label: m.name || m.email || m.user_id,
      hasNpi: !!m.billing_info?.npi_number,
    }));

  const persist = useCallback(
    async (next: { billUnder: string; rules: RuleRow[] }) => {
      const payerOverrides: MemberPayerBillingOverride[] = next.rules
        .filter(rule => rule.payer_uuid)
        .map(rule => ({
          payer_uuid: rule.payer_uuid,
          bill_under_user_id: rule.bill_under_user_id,
        }));
      await updateBillingInfo({
        userId,
        updateMemberBillingInfoRequest: {
          bill_under_user_id: next.billUnder || null,
          payer_billing_overrides: payerOverrides,
        },
      }).unwrap();
    },
    [updateBillingInfo, userId],
  );

  const { saveImmediately, status, lastSavedAt, hasPendingSave } = useAutoSave({
    onSave: persist,
    debounceMs: 2000,
    flushOnUnmount: true,
  });

  const handleDefaultChange = (next: string) => {
    setBillUnderUserId(next);
    saveImmediately({ billUnder: next, rules });
    clinicians.memberBillUnderChanged({
      member_id: userId,
      scope: 'all_payers',
      has_target: next !== SELF,
      payer_rule_count: rules.filter(r => r.payer_uuid).length,
    });
  };

  const handleRulesChange = (nextRules: RuleRow[], persistNow: boolean) => {
    setRules(nextRules);
    if (persistNow) {
      saveImmediately({ billUnder: billUnderUserId, rules: nextRules });
      clinicians.memberBillUnderChanged({
        member_id: userId,
        scope: 'payer_rule',
        has_target: billUnderUserId !== SELF,
        payer_rule_count: nextRules.filter(r => r.payer_uuid).length,
      });
    }
  };

  const renderMemberSelect = (
    value: string,
    onChange: (next: string) => void,
    selfLabel: string,
    label: string,
  ) => (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
      sx={{ flex: 1, minWidth: 0 }}
    >
      <MenuItem value={SELF}>{selfLabel}</MenuItem>
      {memberOptions.map(option => (
        <MenuItem key={option.userId} value={option.userId} disabled={!option.hasNpi}>
          {option.label}
          {!option.hasNpi && ' — no NPI on file'}
        </MenuItem>
      ))}
    </TextField>
  );

  const needsBillUnderSetup = !billingInfo?.npi_number && billUnderUserId === SELF;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Supervisory Claim Submission
          </Typography>
          <Chip label="Optional" size="small" variant="soft" />
        </Box>
        <AutosaveStatus
          status={status}
          isDirty={hasPendingSave()}
          lastSavedAt={lastSavedAt}
          verbose
        />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, mb: 2 }}>
        Only for clinicians whose claims must go out under another member&apos;s NPI —
        for example interns without their own NPI, or clinicians waiting on payer
        credentialing. Most clinicians should leave this set to their own NPI.
        Earnings and reports always attribute to the clinician who delivered the
        session.
      </Typography>

      {needsBillUnderSetup ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This clinician has no NPI on file. Their claims will be blocked unless you
          select a supervising provider below (or add their NPI under Billing &amp;
          Claims).
        </Alert>
      ) : null}

      <Box sx={{ display: 'flex', gap: 2 }}>
        {renderMemberSelect(
          billUnderUserId,
          handleDefaultChange,
          'This clinician (their own NPI)',
          'Submit claims under',
        )}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, mb: 1 }}>
        Payer exceptions override the setting above for specific payers.
      </Typography>

      {rules.map((rule, index) => (
        <Box
          key={rule.payer_uuid || `draft-${index}`}
          sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5 }}
        >
          <PayerPicker
            value={rule.payer_uuid}
            disabled={disabled}
            onChange={payerUuid => {
              const next = rules.map((r, i) => (i === index ? { ...r, payer_uuid: payerUuid } : r));
              handleRulesChange(next, true);
            }}
          />
          {renderMemberSelect(
            rule.bill_under_user_id ?? SELF,
            target => {
              const next = rules.map((r, i) =>
                i === index ? { ...r, bill_under_user_id: target || null } : r,
              );
              // Draft rows (no payer yet) only persist once a payer is chosen.
              handleRulesChange(next, !!rule.payer_uuid);
            },
            'This clinician (their own NPI)',
            'Bill under',
          )}
          <IconButton
            aria-label="Remove payer exception"
            size="small"
            disabled={disabled}
            onClick={() => {
              const next = rules.filter((_, i) => i !== index);
              handleRulesChange(next, !!rule.payer_uuid);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}

      <Button
        size="small"
        startIcon={<AddIcon />}
        disabled={disabled || rules.some(rule => !rule.payer_uuid)}
        onClick={() => handleRulesChange([...rules, { payer_uuid: '', bill_under_user_id: null }], false)}
      >
        Add payer exception
      </Button>
    </Box>
  );
}
